// Template Designer Types

export type DocumentType = 'invoice' | 'quote' | 'credit_memo' | 'purchase_order';

// Line item structure matching the Django model
export type LineItem = {
  name: string;
  code: string;
  description: string;
  quantity: number;
  rate: number;
  item_type: 'product_parts' | 'labor' | 'other';
  discount: number;
  discount_type: 'percentage' | 'fixed';
  tax_rate: number; // Tax percentage (e.g., 10 for 10%)
  tax_amount: number;
  amount: number;
  computed_rate?: number;  // rate after markup applied (for display)
  computed_amount?: number;  // amount after markup applied (for calculations)
};

export type LineItemSection = [string, LineItem[]];

// Business data for the "from" section
export interface BusinessData {
  id?: string;
  name: string;
  address: string;
  industry?: string;
  logo?: string;
  phone?: string;
  email?: string;
  payment_instructions?: string;
  currency_code?: string;
}

// Recipient type (client or vendor)
export interface Recipient {
  id?: string;
  billing_name: string;
  company_name: string;
  address: string;
  emails?: string[];
  phones?: { country_code: string; number: string }[];
  payment_terms?: string;
}

// Signature types for Owl Ops API
export interface DocumentSignature {
  role: 'business' | 'client';
  label: string;
  name: string;
  title?: string;
  company?: string;
  image?: string;
  signed_at: string;
}

export interface SignatureLine {
  role: 'business' | 'client';
  label: string;
}

export interface DocumentContract {
  content: string;
  requires_business_signature?: boolean;
  requires_client_signature?: boolean;
}

// Base document fields shared by all document types
export interface BaseDocumentData {
  id: string;
  line_items: LineItemSection[];
  internal_notes?: string;
  attachments?: unknown[];
  subtotal: number;
  total_tax?: number;
  total_amount: number;
  // Signature and contract fields for Owl Ops rendering
  signatures?: DocumentSignature[];
  signature_lines?: SignatureLine[];
  contract?: DocumentContract;
}

// Invoice data
export interface InvoiceData extends BaseDocumentData {
  documentType: 'invoice';
  invoice_number: string;
  client?: Recipient;
  client_name: string;
  client_company: string;
  client_address: string;
  invoice_date: string;
  due_date: string;
  payment_terms: string;
  status: string;
  deposit?: number;
  stripe_card_enabled?: boolean;
  stripe_ach_enabled?: boolean;
  // Shipping fields
  ship_enabled?: boolean;
  ship_fee?: string | number | null;
  ship_date?: string | null;
  ship_via?: string;
  ship_tracking_number?: string;
  requires_my_signature?: boolean;
  requires_client_signature?: boolean;
  note_for_client: string;
  markup_dollars?: number;
  markup_percent?: number;
  discount_dollars?: number;
  discount_percent?: number;
  total_markup?: number;
  total_discount?: number;
  amount_paid: number;
  balance_due: number;
}

// Quote data
export interface QuoteData extends BaseDocumentData {
  documentType: 'quote';
  quote_number: string;
  client?: Recipient;
  client_name: string;
  client_company: string;
  client_address: string;
  quote_date: string;
  due_date: string;
  status: string;
  deposit?: number;
  stripe_card_enabled?: boolean;
  stripe_ach_enabled?: boolean;
  // Shipping fields
  ship_enabled?: boolean;
  ship_fee?: string | number | null;
  ship_date?: string | null;
  ship_via?: string;
  ship_tracking_number?: string;
  requires_my_signature?: boolean;
  requires_client_signature?: boolean;
  note_for_client: string;
  markup_dollars?: number;
  markup_percent?: number;
  discount_dollars?: number;
  discount_percent?: number;
  total_markup?: number;
  total_discount?: number;
  amount_paid?: number;
  balance_due?: number;
  deposit_fully_paid?: boolean;
}

// Credit memo data
export interface CreditMemoData extends BaseDocumentData {
  documentType: 'credit_memo';
  credit_memo_number: string;
  client?: Recipient;
  client_name: string;
  client_company: string;
  client_address: string;
  credit_memo_date: string;
  status: string;
  note_for_client: string;
  amount_allocated?: number;
  balance_remaining?: number;
  fully_allocated?: boolean;
}

// Purchase order data
export interface PurchaseOrderData extends BaseDocumentData {
  documentType: 'purchase_order';
  purchase_order_number: string;
  vendor?: Recipient;
  vendor_name: string;
  vendor_company: string;
  vendor_address: string;
  issued_date: string;
  status: string;
  markup_dollars?: number;
  markup_percent?: number;
  // Shipping fields
  ship_enabled?: boolean;
  ship_fee?: string | number | null;
  ship_date?: string | null;
  ship_via?: string;
  ship_tracking_number?: string;
  note_for_vendor?: string;
  total_markup?: number;
}

// Discriminated union of all document types
export type DocumentData = InvoiceData | QuoteData | CreditMemoData | PurchaseOrderData;

// Options for generateDocument function
export interface GenerateDocumentOptions {
  data: DocumentData;
  templateLayout: string;
  settings: TemplateSettings;
  business: BusinessData;
}

// LogoAlignment removed - not used in rendering

export type BannerStyle =
  | 'none'
  | 'solid'
  | 'gradient'
  | 'flowing-curves'      // Elegant S-curve with layered waves
  | 'organic-blob'        // Modern organic blob shapes
  | 'angular-sweep'       // Bold diagonal sweep with accent stripe
  | 'layered-waves'       // Multiple flowing wave layers
  | 'mesh-gradient'       // Soft mesh-like gradient blending
  | 'ribbon-accent'       // Diagonal ribbon with secondary accent
  | 'circuit-lines'       // Tech-inspired fine line pattern
  | 'topographic'         // Topographic contour lines
  | 'aurora';             // Northern lights inspired gradient flow

export type WatermarkPosition = 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'diagonal';

export interface Logo {
  url: string | null;
  size: number; // 1-100
  showCompanyName: boolean;
  /** Pre-rendered SVG for placeholder icons (for Owl Ops) */
  iconSvg?: string;
}

// Attachment interface removed - not implemented

export interface ColorTheme {
  primary: string;
  secondary: string;
  accent: string;
  headerBg: string;
  headerText: string;
  tableBorder: string;
  tableHeaderBg: string;
  tableHeaderText: string;
  tableAltRow: string;
  divider: string;
  total: string;
}

export interface Banner {
  enabled: boolean;
  style: BannerStyle;
  height: number;
  /** Pre-rendered HTML for the banner (generated from style + colors for Owl Ops) */
  html?: string;
}

export interface Watermark {
  enabled: boolean;
  type: 'text' | 'logo' | 'icon';
  content: string;
  opacity: number;
  size: number;
  position: WatermarkPosition;
  rotation: number;
  /** Pre-rendered SVG for icon watermarks (for Owl Ops) */
  iconSvg?: string;
}

export interface HeaderField {
  id: string;
  name: string;
  enabled: boolean;
  isCustom: boolean;
  defaultValue?: string;
  documentTypes: DocumentType[];
}

export interface TableColumn {
  id: string;
  name: string;
  enabled: boolean;
  width?: number;
}

export interface FooterSettings {
  genericNote: {
    enabled: boolean;
    content: string;
  };
  documentNotes: {
    [key in DocumentType]: {
      enabled: boolean;
      content: string;
    };
  };
  termsAndConditions: {
    enabled: boolean;
    content: string;
  };
  taxBreakdown: boolean;
  showPaymentInstructions: boolean;
  // Signature lines now driven by document-level requires_my_signature/requires_client_signature
}

export interface Labels {
  documentHeadings: Record<string, string>;
  general: Record<string, string>;
  documentSummary: Record<string, string>;
  remittance: Record<string, string>;
}

export interface TemplateLayout {
  id: string;
  name: string;
  description: string;
  thumbnail: string;
  category: 'classic' | 'modern' | 'minimal' | 'bold';
}

export interface TemplateSettings {
  // Layout
  selectedLayout: string;

  // Branding
  logo: Logo;
  colorTheme: ColorTheme;
  banner: Banner;
  watermark: Watermark;

  // Document Options
  headerFields: HeaderField[];
  tableColumns: TableColumn[];
  footer: FooterSettings;

  // Labels
  labels: Labels;

  // General
  dateFormat: string;
}

export const DEFAULT_TEMPLATE_SETTINGS: TemplateSettings = {
  selectedLayout: 'standard',

  logo: {
    url: null,
    size: 60,
    showCompanyName: true,
  },

  colorTheme: {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    accent: '#06b6d4',
    headerBg: '#f8fafc',
    headerText: '#0f172a',
    tableBorder: '#e2e8f0',
    tableHeaderBg: '#f1f5f9',
    tableHeaderText: '#334155',
    tableAltRow: '#fafafa',
    divider: '#e2e8f0',
    total: '#0f172a',
  },

  banner: {
    enabled: false,
    style: 'gradient',
    height: 80,
  },

  watermark: {
    enabled: false,
    type: 'icon',
    content: 'icon:briefcase',
    opacity: 10,
    size: 50,
    position: 'center',
    rotation: -45,
  },

  headerFields: [
    { id: 'ship_to', name: 'Ship To', enabled: false, isCustom: false, documentTypes: ['invoice', 'quote', 'credit_memo', 'purchase_order'] },
    { id: 'ship_date', name: 'Ship Date', enabled: false, isCustom: false, documentTypes: ['invoice', 'quote', 'purchase_order'] },
    { id: 'ship_via', name: 'Ship Via', enabled: false, isCustom: false, documentTypes: ['invoice', 'quote', 'purchase_order'] },
    { id: 'tracking_no', name: 'Tracking No', enabled: false, isCustom: false, documentTypes: ['invoice', 'quote', 'purchase_order'] },
    { id: 'date', name: 'Date', enabled: true, isCustom: false, documentTypes: ['invoice', 'quote', 'credit_memo', 'purchase_order'] },
    { id: 'due_date', name: 'Due Date', enabled: true, isCustom: false, documentTypes: ['invoice', 'quote'] },
    { id: 'terms', name: 'Payment Terms', enabled: true, isCustom: false, documentTypes: ['invoice'] },
  ],

  tableColumns: [
    { id: 'name', name: 'Item', enabled: true, width: 40 },
    { id: 'code', name: 'Code', enabled: false, width: 10 },
    { id: 'description', name: 'Description', enabled: false, width: 20 },
    { id: 'quantity', name: 'Qty', enabled: true, width: 10 },
    { id: 'rate', name: 'Rate', enabled: true, width: 15 },
    { id: 'tax', name: 'Tax', enabled: true, width: 10 },
    { id: 'amount', name: 'Amount', enabled: true, width: 25 },
  ],

  footer: {
    genericNote: { enabled: false, content: 'Thank you for your business!' },
    documentNotes: {
      invoice: { enabled: false, content: 'Payment is due within the specified terms.' },
      quote: { enabled: false, content: 'This quote is valid for 30 days.' },
      credit_memo: { enabled: false, content: 'This credit will be applied to your account.' },
      purchase_order: { enabled: false, content: 'Please confirm receipt of this order.' },
    },
    termsAndConditions: { enabled: false, content: '' },
    taxBreakdown: true,
    showPaymentInstructions: true,
  },

  labels: {
    // Keys match Owl Ops API expectations (camelCase)
    documentHeadings: {
      Invoice: 'Invoice',
      Quote: 'Quote',
      CreditMemo: 'Credit Memo',
      PurchaseOrder: 'Purchase Order',
      Receipt: 'Receipt',
    },
    general: {
      BillTo: 'Bill To',
      ShipTo: 'Ship To',
      Vendor: 'Vendor',
      Date: 'Date',
      Due: 'Due',
      ValidUntil: 'Valid Until',
      Terms: 'Terms',
      Notes: 'Notes',
      // Column labels
      Item: 'Item',
      Code: 'Code',
      Description: 'Description',
      Quantity: 'Qty',
      Rate: 'Rate',
      Tax: 'Tax',
      Amount: 'Amount',
    },
    documentSummary: {
      subtotal: 'Subtotal',
      tax: 'Tax',
      discount: 'Discount',
      markup: 'Markup',
      total: 'Total',
      paid: 'Amount Paid',
      balance: 'Balance Due',
      deposit: 'Deposit Due',
    },
    remittance: {
      BalanceDue: 'Balance Due',
      QuoteTotal: 'Quote Total',
      CreditAmount: 'Credit Amount',
      AmountPaid: 'Amount Paid',
    },
  },

  dateFormat: 'MM/dd/yyyy',
};
